import React from "react";
import { IDS } from "@/constants/testIds";
import {
  Cpu,
  Network,
  Coins,
  Server,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    code: "01",
    icon: Cpu,
    title: "Smart Contract Development",
    sub: "Solana · Rust · Anchor",
    desc: "Production Solana programs and EVM contracts. Anchor-based monorepos, fuzz testing, formal verification hooks, and gas-conscious bytecode. We treat every program like it already holds 9 figures.",
    tags: ["Anchor", "Rust", "Foundry", "Fuzz / Echidna"],
  },
  {
    code: "02",
    icon: Network,
    title: "Protocol / L2 Architecture",
    sub: "design · spec · upgrade-paths",
    desc: "End-to-end protocol design: state machines, fee markets, sequencing, bridging, and upgrade safety. We architect rollups and appchains that survive contact with mainnet — and adversarial users.",
    tags: ["Rollup design", "Fee market", "Bridging", "Sequencer ops"],
  },
  {
    code: "03",
    icon: Coins,
    title: "Token Launch & Tokenomics",
    sub: "issuance · vesting · liquidity",
    desc: "Token issuance, vesting contracts, and liquidity bootstrapping with adversarial review. Sim-tested supply schedules, treasury policies, and on-chain governance plumbing.",
    tags: ["SPL / ERC-20", "Vesting", "TGE ops", "Liquidity"],
  },
  {
    code: "04",
    icon: Server,
    title: "Node Infrastructure & Indexers",
    sub: "validators · rpc · indexing",
    desc: "Validator fleets, hardened RPC clusters, custom indexers and stream consumers. From Firedancer-ready Solana validators to gRPC pipelines — we run the metal, you ship the product.",
    tags: ["Validators", "RPC", "Geyser/Yellowstone", "Indexers"],
  },
];

const Services = () => {
  return (
    <section
      id="services"
      data-testid={IDS.services}
      className="relative py-28 lg:py-36"
    >
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <SectionHeader
          kicker="// 01 — Services"
          title="What we build for chains, protocols, and treasuries."
          right="cat services.md"
        />

        <div className="mt-14 grid gap-px bg-[rgba(0,255,65,0.18)] border border-[rgba(0,255,65,0.18)] md:grid-cols-2">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.code}
                data-testid={IDS.serviceCard(i)}
                className="group relative bg-[#0a0a0a] p-8 lg:p-10 transition-colors hover:bg-[#0d0d0d]"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-zinc-500">
                    <span className="text-[#00FF41]">[{s.code}]</span>
                    <span>{s.sub}</span>
                  </div>
                  <ArrowUpRight
                    size={18}
                    strokeWidth={1.4}
                    className="text-zinc-700 group-hover:text-[#00FF41] transition-colors"
                  />
                </div>

                <div className="mt-8 flex items-center gap-4">
                  <span className="grid place-items-center w-11 h-11 border border-[rgba(0,255,65,0.35)] text-[#00FF41] group-hover:bg-[#00FF41] group-hover:text-black transition-colors">
                    <Icon size={20} strokeWidth={1.4} />
                  </span>
                  <h3 className="font-mono text-xl sm:text-2xl tracking-tight text-white">
                    {s.title}
                  </h3>
                </div>

                <p className="mt-6 text-sm sm:text-[15px] leading-relaxed text-zinc-400 max-w-[52ch]">
                  {s.desc}
                </p>

                <div className="mt-7 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] uppercase tracking-[0.22em] border border-[rgba(255,255,255,0.08)] text-zinc-400 px-2.5 py-1"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                {/* corner ticks */}
                <Tick className="top-3 left-3" />
                <Tick className="top-3 right-3" rotate="90deg" />
                <Tick className="bottom-3 left-3" rotate="-90deg" />
                <Tick className="bottom-3 right-3" rotate="180deg" />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Tick = ({ className = "", rotate = "0deg" }) => (
  <span
    aria-hidden
    className={`absolute pointer-events-none ${className}`}
    style={{ transform: `rotate(${rotate})` }}
  >
    <span className="block w-3 h-px bg-[#00FF41]/60" />
    <span className="block w-px h-3 bg-[#00FF41]/60" />
  </span>
);

export const SectionHeader = ({ kicker, title, right }) => (
  <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
    <div className="max-w-2xl">
      <div className="text-[11px] uppercase tracking-[0.32em] text-[#00FF41]">
        {kicker}
      </div>
      <h2 className="mt-4 font-mono font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-[44px] leading-[1.08]">
        {title}
      </h2>
    </div>
    {right && (
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
        <span className="text-[#00FF41]">$</span> {right}
      </div>
    )}
  </div>
);

export default Services;
